/**
 * AlertPill — canonical alert/warning badge component.
 *
 * Used for: Internal Only flags, Warnings, Alerts, Errors, and other notification badges.
 *
 * Color scheme by severity:
 *   Error       → red-100 (critical)
 *   Warning     → orange-100 (caution)
 *   Info        → blue-100 (informational)
 *   Success     → green-100 (positive)
 *
 * Usage:
 *   <AlertPill type="error" text="Internal Only" />
 *   <AlertPill type="warning" text="Review Required" />
 *   <AlertPill type="info" text="In Progress" />
 */

import React from 'react';

// ─── Config type ──────────────────────────────────────────────────────────────

export interface AlertConfig {
  /** Light pill background (e.g. bg-red-100) */
  bg: string;
  /** Pill text color (e.g. text-red-700) */
  text: string;
  /** Pill border color (e.g. border-red-200) */
  border: string;
}

// ─── Canonical map ────────────────────────────────────────────────────────────

const BASE = 'font-semibold text-xs rounded border';

const ALERT_MAP: Record<string, AlertConfig> = {
  // ── Error (red) ─────────────────────────────────────────────────────────────
  ERROR: {
    bg: 'bg-red-100',
    text: 'text-red-700',
    border: 'border-red-200',
  },

  // ── Warning (orange) ────────────────────────────────────────────────────────
  WARNING: {
    bg: 'bg-orange-100',
    text: 'text-orange-700',
    border: 'border-orange-200',
  },

  // ── Info (blue) ─────────────────────────────────────────────────────────────
  INFO: {
    bg: 'bg-blue-100',
    text: 'text-blue-700',
    border: 'border-blue-200',
  },

  // ── Success (green) ─────────────────────────────────────────────────────────
  SUCCESS: {
    bg: 'bg-green-100',
    text: 'text-green-700',
    border: 'border-green-200',
  },

  // ── Default (gray) ─────────────────────────────────────────────────────────
  DEFAULT: {
    bg: 'bg-gray-100',
    text: 'text-gray-700',
    border: 'border-gray-200',
  },
};

const DEFAULT_CONFIG: AlertConfig = ALERT_MAP.DEFAULT;

// ─── Utility functions ────────────────────────────────────────────────────────

/**
 * Look up the canonical config for an alert type.
 * Normalises to uppercase; falls back to DEFAULT for unknown types.
 */
export function getAlertConfig(type: string): AlertConfig {
  if (!type) return DEFAULT_CONFIG;
  const key = type.trim().toUpperCase();
  return ALERT_MAP[key] ?? DEFAULT_CONFIG;
}

/**
 * Returns the full Tailwind class string for an alert pill.
 *
 * @example
 *   className={`${getAlertPillClassName('error')}`}
 */
export function getAlertPillClassName(type: string): string {
  const config = getAlertConfig(type);
  return `${config.bg} ${config.text} ${config.border} ${BASE}`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export interface AlertPillProps {
  /** Alert type: 'error' | 'warning' | 'info' | 'success' | 'default' */
  type: string;
  /** Display text content (e.g. "Internal Only", "Review Required") */
  text: React.ReactNode;
  /** Extra Tailwind classes appended to the root element */
  className?: string;
}

/**
 * AlertPill — canonical alert/warning badge component.
 *
 * Renders as: font-semibold · text-xs · rounded · border + type-specific
 * light background, colored text, and matching border.
 *
 * Standardized layout: inline-block · px-2 py-0.5
 */
export function AlertPill({
  type,
  text,
  className = '',
}: AlertPillProps) {
  return (
    <span
      className={`inline-block px-2 py-0.5 ${getAlertPillClassName(type)} ${className}`.trim()}
    >
      {text}
    </span>
  );
}

export default AlertPill;
